import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useTransform, animate, useDragControls } from 'framer-motion';
import { CaretUp, Crosshair, MagnifyingGlass, SlidersHorizontal } from 'phosphor-react';
import { addDays, endOfDay, endOfMonth, endOfWeek, format, startOfDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useActiveCenter } from '@/hooks/useActiveCenter';
import { EventMap, MapPopupCard } from '@/components/EventMap';
import { Chip } from '@/components/ui/Chip';
import { EventCard } from '@/components/EventCard';
import { CATEGORY_LIST } from '@/constants/categories';
import type { EventSearchRow } from '@/types/database';
import { hap } from '@/lib/haptics';
import { EditorialDateRangePicker } from '@/components/ui/EditorialDateRangePicker';
import { formatRangeChip } from '@/lib/dates';
import { collectKinds, cycleEventKindFilter, type EventKindFilters } from '@/lib/eventKindFilters';
import { haversineKm, viewportRadiusKm, type LatLon } from '@/lib/geo';
import { AdExplorerListItem } from '@/components/ads/AdExplorerListItem';
import { interleaveAds, isAdMarker, AD_FREQUENCY } from '@/lib/ads';

const SNAPS = { peek: 0.12, half: 0.5, full: 0.92 };
const DEFAULT_CENTER = { lat: 46.603354, lon: 1.888334, label: 'France' };
const PERIOD_OPTIONS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'weekend', label: 'Ce week-end' },
  { id: 'week', label: 'Cette semaine' },
  { id: 'month', label: 'Ce mois' },
  { id: 'year', label: 'Cette année' },
  { id: 'range', label: 'Période' },
] as const;

type PeriodFilter = typeof PERIOD_OPTIONS[number]['id'];
type ViewportBounds = { north: number; south: number; east: number; west: number };

const SEARCH_RADIUS_DEFAULT_KM = 50;
const SEARCH_RADIUS_MAX_KM = 300;

function rangeFor(period: PeriodFilter, selectedFrom: string, selectedTo: string) {
  const now = new Date();

  if (period === 'today') {
    return { from: now.toISOString(), to: endOfDay(now).toISOString() };
  }

  if (period === 'weekend') {
    const saturday = addDays(startOfDay(now), (6 - now.getDay() + 7) % 7);
    return { from: saturday.toISOString(), to: endOfWeek(saturday, { weekStartsOn: 1 }).toISOString() };
  }

  if (period === 'month') {
    return { from: now.toISOString(), to: endOfMonth(now).toISOString() };
  }

  if (period === 'year') {
    return { from: now.toISOString(), to: addDays(now, 365).toISOString() };
  }

  if (period === 'range') {
    const from = startOfDay(new Date(`${selectedFrom}T12:00:00`));
    const to = endOfDay(new Date(`${selectedTo}T12:00:00`));
    return { from: from.toISOString(), to: to.toISOString() };
  }

  return { from: now.toISOString(), to: addDays(now, 7).toISOString() };
}

export default function Explorer() {
  const nav = useNavigate();
  const activeCenter = useActiveCenter();
  const [kindFilters, setKindFilters] = useState<EventKindFilters>(() => {
    try { return JSON.parse(sessionStorage.getItem('discover:kindFilters') ?? '{}'); } catch { return {}; }
  });
  const [events, setEvents] = useState<EventSearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockingLoading, setBlockingLoading] = useState(true);
  const [snap, setSnap] = useState<keyof typeof SNAPS>('half');
  const [winH, setWinH] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
  const [period, setPeriod] = useState<PeriodFilter>(() => {
    const t = sessionStorage.getItem('discover:time');
    const map: Record<string, PeriodFilter> = { now: 'today', tonight: 'today', tomorrow: 'today', weekend: 'weekend', week: 'week', month: 'month', year: 'year', range: 'range' };
    return (t && map[t]) ? map[t] : 'month';
  });
  const [selectedFrom, setSelectedFrom] = useState(() => sessionStorage.getItem('discover:selectedFrom') ?? format(new Date(), 'yyyy-MM-dd'));
  const [selectedTo, setSelectedTo] = useState(() => sessionStorage.getItem('discover:selectedTo') ?? format(new Date(), 'yyyy-MM-dd'));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [viewport, setViewport] = useState<{ center: LatLon; bounds: ViewportBounds } | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventSearchRow | null>(null);
  const snapBeforePopup = useRef<keyof typeof SNAPS | null>(null);
  const savedScrollTop = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const fallbackCenter = useMemo(() => ({
    lat: activeCenter.lat ?? DEFAULT_CENTER.lat,
    lon: activeCenter.lon ?? DEFAULT_CENTER.lon,
  }), [activeCenter.lat, activeCenter.lon]);
  const [mapCenter, setMapCenter] = useState(fallbackCenter);
  const [searchCenter, setSearchCenter] = useState(fallbackCenter);
  const [searchRadius, setSearchRadius] = useState(SEARCH_RADIUS_DEFAULT_KM);
  const includedKinds = useMemo(() => collectKinds(kindFilters, 'include'), [kindFilters]);
  const excludedKinds = useMemo(() => collectKinds(kindFilters, 'exclude'), [kindFilters]);
  const excludedKindsSet = useMemo(() => new Set(excludedKinds), [excludedKinds]);

  useEffect(() => {
    function onR() { setWinH(window.innerHeight); }
    window.addEventListener('resize', onR); return () => window.removeEventListener('resize', onR);
  }, []);

  useEffect(() => {
    setMapCenter(fallbackCenter);
    setSearchCenter(fallbackCenter);
    setSearchRadius(SEARCH_RADIUS_DEFAULT_KM);
  }, [fallbackCenter]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { from, to } = rangeFor(period, selectedFrom, selectedTo);
        const params = {
          p_lat: searchCenter.lat, p_lon: searchCenter.lon, p_radius_km: searchRadius,
          p_kinds: includedKinds.length ? includedKinds : null, p_audiences: null,
          p_from: from, p_to: to, p_limit: 400,
        };
        let lastError: unknown = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          const { data, error } = await supabase.rpc('search_events', params);
          if (!alive) return;
          if (!error) {
            const rows = (data as EventSearchRow[]) ?? [];
            setEvents(excludedKinds.length ? rows.filter((row) => !excludedKindsSet.has(row.kind)) : rows);
            return;
          }
          lastError = error;
          if (attempt < 2) await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
          if (!alive) return;
        }
        console.error(lastError);
        setEvents([]);
      } finally {
        if (alive) {
          setLoading(false);
          setBlockingLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [excludedKinds.length, excludedKindsSet, includedKinds, period, searchCenter.lat, searchCenter.lon, searchRadius, selectedFrom, selectedTo]);

  const sheetY = useMotionValue(winH * (1 - SNAPS.half));
  const sheetHeight = useTransform(sheetY, y => `${winH - y}px`);

  function snapTo(s: keyof typeof SNAPS) {
    setSnap(s); hap.selection();
    animate(sheetY, winH * (1 - SNAPS[s]), { type: 'spring', stiffness: 300, damping: 30 });
  }

  const dragControls = useDragControls();
  function handleEventSelect(id: string) {
    const event = events.find(e => e.id === id);
    if (!event) return;
    if (snapBeforePopup.current === null) {
      snapBeforePopup.current = snap;
      savedScrollTop.current = listRef.current?.scrollTop ?? 0;
    }
    setSelectedEvent(event);
    snapTo('peek');
  }

  function handlePopupClose() {
    setSelectedEvent(null);
    if (snapBeforePopup.current !== null) {
      const prevSnap = snapBeforePopup.current;
      snapBeforePopup.current = null;
      const scrollTop = savedScrollTop.current;
      snapTo(prevSnap);
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = scrollTop;
      });
    }
  }

  const rangeChipLabel = formatRangeChip(`${selectedFrom}T12:00:00`, `${selectedTo}T12:00:00`);
  const visibleEvents = useMemo(() => {
    const bounds = viewport?.bounds;
    if (!bounds) return events;
    return events.filter((event) => (
      event.lat != null
      && event.lon != null
      && event.lat <= bounds.north
      && event.lat >= bounds.south
      && event.lon <= bounds.east
      && event.lon >= bounds.west
    ));
  }, [events, viewport]);

  // On propose de rechercher la zone affichée dès qu'une partie du viewport sort
  // du périmètre déjà chargé (l'utilisateur a déplacé/dézoomé la carte).
  const canSearchArea = useMemo(() => {
    if (!viewport) return false;
    const moved = haversineKm(viewport.center, searchCenter);
    const vr = viewportRadiusKm(viewport.center, viewport.bounds);
    return moved + vr > searchRadius;
  }, [viewport, searchCenter, searchRadius]);

  function searchThisArea() {
    if (!viewport) return;
    hap.selection();
    const vr = viewportRadiusKm(viewport.center, viewport.bounds);
    // On charge un peu plus large que le visible pour éviter de re-déclencher au moindre pan.
    const radius = Math.min(Math.max(Math.round(vr * 1.15), 5), SEARCH_RADIUS_MAX_KM);
    setSearchRadius(radius);
    setSearchCenter({ lat: viewport.center.lat, lon: viewport.center.lon });
    setBlockingLoading(true);
  }

  return (
    <>
    <div className="fixed inset-0 bg-paper">
      <div className="absolute inset-0">
        <EventMap
          events={events}
          center={mapCenter}
          userCenter={activeCenter.mode !== 'default' ? { lat: activeCenter.lat, lon: activeCenter.lon } : null}
          showUserPin={activeCenter.mode !== 'default'}
          height="100%"
          onEventClick={handleEventSelect}
          onViewportChange={(v) => setViewport({ center: v.center, bounds: v.bounds })}
        />
      </div>

      <AnimatePresence>
        {canSearchArea && !blockingLoading && (
          <motion.button
            key="search-area"
            type="button"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: 'spring', stiffness: 360, damping: 30 }}
            onClick={searchThisArea}
            className="absolute left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 rounded-full bg-ink-1 px-4 h-10 text-[13px] font-medium text-paper-elev shadow-sheet"
            style={{ top: 'max(env(safe-area-inset-top), 16px)' }}
          >
            <MagnifyingGlass size={15} weight="bold" />
            Rechercher dans cette zone
          </motion.button>
        )}
      </AnimatePresence>

      {blockingLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-paper/72 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-paper-elev px-8 py-7 ring-1 ring-paper-edge shadow-sheet">
            <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-paper-edge border-t-terre" aria-hidden="true" />
            <span className="t-ui-sm text-ink-2">Chargement des sorties…</span>
          </div>
        </div>
      )}

      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            key="event-overlay"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="absolute top-0 left-0 right-0 z-25 flex justify-center px-4 pt-[max(env(safe-area-inset-top),52px)]"
          >
            <MapPopupCard
              event={selectedEvent}
              onOpenDetails={() => { nav(`/event/${selectedEvent.id}`); }}
              onClose={handlePopupClose}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => {
          hap.selection();
          setMapCenter(fallbackCenter);
          setSearchCenter(fallbackCenter);
          setSearchRadius(SEARCH_RADIUS_DEFAULT_KM);
        }}
        className="absolute right-4 z-10 h-11 w-11 rounded-full grid place-items-center bg-paper-elev/95 backdrop-blur ring-1 ring-paper-edge shadow-sheet"
        style={{ bottom: `calc(${winH * SNAPS[snap]}px + 12px)` }}>
        <Crosshair size={18} />
      </button>

      <motion.div
        style={{ height: sheetHeight, y: sheetY, top: 0 }}
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: winH * (1 - SNAPS.full), bottom: winH * (1 - SNAPS.peek) }}
        dragElastic={0.05}
        onDrag={(_, info) => sheetY.set(Math.max(winH * (1 - SNAPS.full), Math.min(winH * (1 - SNAPS.peek), sheetY.get() + info.delta.y)))}
        onDragEnd={(_, info) => {
          const cur = sheetY.get(); const v = info.velocity.y;
          const candidates = [SNAPS.peek, SNAPS.half, SNAPS.full]
            .map(p => ({ p, dist: Math.abs(winH * (1 - p) - (cur + v * 0.2)) }))
            .sort((a, b) => a.dist - b.dist);
          const next = candidates[0].p;
          const key = (Object.keys(SNAPS) as (keyof typeof SNAPS)[]).find(k => SNAPS[k] === next) ?? 'half';
          snapTo(key);
        }}
        className="absolute left-0 right-0 bg-paper-elev rounded-t-xl ring-1 ring-paper-edge shadow-sheet z-20 overflow-hidden"
      >
        <div
          onClick={() => {
            if (snap === 'peek') snapTo('half');
          }}
          onPointerDown={(event) => {
            if ((event.target as HTMLElement).closest('button')) return;
            dragControls.start(event);
          }}
          className="cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="pt-3 pb-2 flex justify-center"><span className="block w-10 h-1 rounded-full bg-champagne" /></div>
            <div className="px-5 pb-2 flex items-baseline gap-3">
              {loading
                ? <h2 className="t-ed-xl font-serif italic">Recherche en cours</h2>
               : visibleEvents.length === 0
               ? <h2 className="t-ed-xl font-serif italic">Rien dans cette zone</h2>
               : <h2 className="t-ed-xl">{visibleEvents.length} sortie{visibleEvents.length > 1 ? 's' : ''}</h2>}
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); hap.selection(); setIsFilterSheetOpen(true); }}
                className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium ring-1 shadow-sm transition ${
                  Object.keys(kindFilters).length > 0 || period !== 'month'
                    ? 'bg-terre text-paper-elev ring-terre/30'
                    : 'bg-paper text-ink-1 ring-paper-edge'
                }`}
              >
                <SlidersHorizontal size={12} />
                Filtrer
              </button>
              {snap === 'peek' && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  snapTo('half');
                }}
                className="ml-auto inline-flex h-8 items-center gap-1 rounded-full bg-paper px-3 text-[12px] font-medium text-ink-1 ring-1 ring-paper-edge shadow-sm"
              >
                Ouvrir <CaretUp size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>

        <div className={snap !== 'peek' ? 'hidden' : 'overflow-x-auto scrollbar-none px-5 pb-4'}>
          <div className="flex gap-4">
            {visibleEvents.map(e => <EventCard key={e.id} event={e} variant="peek" onTap={() => handleEventSelect(e.id)} />)}
          </div>
        </div>
        <div
          ref={listRef}
          className={`overflow-y-auto px-5 pb-[env(safe-area-inset-bottom)] ${snap === 'peek' ? 'hidden' : ''}`}
          style={{ height: 'calc(100% - 80px)' }}
        >
          <div className="divide-y divide-paper-edge">
            {interleaveAds(visibleEvents, AD_FREQUENCY.explorer_list_interval, AD_FREQUENCY.explorer_first_at).map((item) => (
              isAdMarker(item)
                ? <AdExplorerListItem key={item.key} adKey={item.key} />
                : <EventCard key={item.id} event={item} variant="row" onTap={() => handleEventSelect(item.id)} />
            ))}
          </div>
        </div>
      </motion.div>
    </div>

      <AnimatePresence>
        {isFilterSheetOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-ink-1/40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsFilterSheetOpen(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 bg-paper rounded-t-2xl shadow-sheet pb-[env(safe-area-inset-bottom)]"
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            >
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-paper-edge" />
              </div>
              <div className="px-5 pb-5">
                {/* Quand */}
                <h3 className="t-cap mb-3">Quand</h3>
                <div className="flex flex-wrap gap-2 mb-5">
                  {PERIOD_OPTIONS.map(option => (
                    <Chip
                      key={option.id}
                      active={period === option.id}
                      onClick={() => {
                        if (option.id === 'range') {
                          setDatePickerOpen(true);
                          return;
                        }
                        if (period !== option.id) {
                          setBlockingLoading(true);
                          setPeriod(option.id);
                        }
                      }}
                    >
                      {option.id === 'range' ? (period === 'range' ? rangeChipLabel : 'Choisir une date') : option.label}
                    </Chip>
                  ))}
                </div>

                <div className="filet mb-5" />

                {/* Catégories */}
                <h3 className="t-cap mb-3">Catégories</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  <Chip
                    active={Object.keys(kindFilters).length === 0}
                    onClick={() => {
                      if (Object.keys(kindFilters).length > 0) {
                        setBlockingLoading(true);
                        setKindFilters({});
                      }
                    }}
                  >
                    Tout
                  </Chip>
                  {CATEGORY_LIST.map(c => (
                    <Chip
                      key={c.id}
                      active={kindFilters[c.id] === 'include'}
                      onClick={() => {
                        setBlockingLoading(true);
                        setKindFilters((current) => cycleEventKindFilter(current, c.id));
                      }}
                    >
                      {c.label}
                    </Chip>
                  ))}
                </div>

                <button
                  onClick={() => setIsFilterSheetOpen(false)}
                  className="w-full h-11 rounded-full bg-ink-1 text-paper-elev t-ui-sm font-medium"
                >
                  Voir les sorties →
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <EditorialDateRangePicker
        open={datePickerOpen}
        selectedFrom={selectedFrom}
        selectedTo={selectedTo}
        onSelect={(from, to) => {
          setBlockingLoading(true);
          setSelectedFrom(from);
          setSelectedTo(to);
          setPeriod('range');
        }}
        onClose={() => setDatePickerOpen(false)}
        title="Choisir une période"
      />
    </>
  );
}
